/**
 * ╔═══════════════════════════════════════════════════════════════════════════╗
 * ║  BUYKEYWARNINGS.TSX - Pre-Transaction Alerts                              ║
 * ║  Shows contextual warnings before purchase                                ║
 * ║  "Surge active, want to wait?" style guidance                             ║
 * ╚═══════════════════════════════════════════════════════════════════════════╝
 */

import { EXTINCTION_LIVE } from '../config/onchainFeatures';
import { useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

export interface BuyWarning {
  id: string;
  type: 'warning' | 'info' | 'opportunity';
  icon: string;
  title: string;
  message: string;
  suggestion?: string;
  action?: {
    label: string;
    onClick: () => void;
  };
}

interface BuyKeysWarningsProps {
  // Game state
  isFOMOSurge?: boolean;
  fomoSurgeEndsIn?: number;
  isFlashCrash?: boolean;
  flashCrashEndsIn?: number;
  isAssassinMode?: boolean;
  extinctionEta?: number;
  isCursed?: boolean;
  timeLeft?: number;
  
  // Player state
  currentKeyPrice?: bigint;
  normalKeyPrice?: bigint;
  estimatedGasUSD?: number;
  
  // Callbacks
  onWait?: () => void;
  
  className?: string;
}

const WARNING_STYLES = {
  warning: 'bg-orange-500/10 border-orange-500/30 text-orange-400',
  info: 'bg-cyan-500/10 border-cyan-500/30 text-cyan-400',
  opportunity: 'bg-green-500/10 border-green-500/30 text-green-400',
};

export function BuyKeysWarnings({
  isFOMOSurge = false,
  fomoSurgeEndsIn = 0,
  isFlashCrash = false,
  flashCrashEndsIn = 0,
  isAssassinMode = false,
  extinctionEta = 60,
  isCursed = false,
  timeLeft = 86400,
  estimatedGasUSD = 0,
  onWait,
  className = '',
}: BuyKeysWarningsProps) {
  const warnings = useMemo(() => {
    const w: BuyWarning[] = [];

    // FOMO Surge - STRONG warning to wait
    if (isFOMOSurge) {
      w.push({
        id: 'fomo_surge',
        type: 'warning',
        icon: '📈',
        title: 'AMPLIFICATION ACTIVE',
        message: `Prices are 2x normal right now.`,
        suggestion: `Wait ${fomoSurgeEndsIn} seconds for normal prices.`,
        action: onWait ? { label: 'Wait', onClick: onWait } : undefined,
      });
    }

    // Flash Crash - Opportunity!
    if (isFlashCrash) {
      w.push({
        id: 'flash_crash',
        type: 'opportunity',
        icon: '⚡',
        title: 'GLITCH DETECTED!',
        message: 'Prices are 50% off!',
        suggestion: `Only ${flashCrashEndsIn}s remaining. Buy now!`,
      });
    }

    // Assassin Mode - Info
    if (isAssassinMode && !isFlashCrash) {
      w.push({
        id: 'assassin',
        type: 'info',
        icon: '🗡️',
        title: 'Terminal Active',
        message: '50% discount. Half of the dividend share goes to the pot.',
      });
    }

    // Extinction imminent - Warning
    if (EXTINCTION_LIVE && extinctionEta > 0 && extinctionEta < 10) {
      w.push({
        id: 'extinction_soon',
        type: 'warning',
        icon: '🌋',
        title: 'Recalibration Likely',
        message: 'Timer may drop 5-30% soon.',
        suggestion: 'Your purchase could be followed by a drop.',
      });
    }

    // Winner's Curse - Info
    if (isCursed) {
      w.push({
        id: 'cursed',
        type: 'info',
        icon: '👻',
        title: 'Singularity Shadow Active',
        message: 'Your purchases add 2x time to timer.',
        suggestion: 'Others only add 30s. You add 60s per key.',
      });
    }

    // Critical timer - Opportunity
    if (timeLeft > 0 && timeLeft < 60) {
      w.push({
        id: 'critical_timer',
        type: 'opportunity',
        icon: '🚨',
        title: 'CRITICAL MOMENT',
        message: 'Under 1 minute! One purchase could win!',
      });
    } else if (timeLeft > 0 && timeLeft < 300) {
      w.push({
        id: 'low_timer',
        type: 'info',
        icon: '🎯',
        title: 'Terminal Phase',
        message: `${Math.floor(timeLeft / 60)}m ${timeLeft % 60}s remaining.`,
        suggestion: 'Good time to position for the win.',
      });
    }

    // High gas warning
    if (estimatedGasUSD > 5) {
      w.push({
        id: 'high_gas',
        type: 'warning',
        icon: '⛽',
        title: 'High Gas',
        message: `Estimated: ~$${estimatedGasUSD.toFixed(2)}`,
        suggestion: 'Base may be congested.',
      });
    }

    return w;
  }, [
    isFOMOSurge,
    fomoSurgeEndsIn,
    isFlashCrash,
    flashCrashEndsIn,
    isAssassinMode,
    extinctionEta,
    isCursed,
    timeLeft,
    estimatedGasUSD,
    onWait,
  ]);

  if (warnings.length === 0) {
    return null;
  }

  return (
    <div className={`space-y-2 ${className}`}>
      <AnimatePresence mode="popLayout">
        {warnings.map((warning) => (
          <motion.div
            key={warning.id}
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            className={`
              p-3 rounded-lg border
              ${WARNING_STYLES[warning.type]}
            `}
          >
            <div className="flex items-start gap-2">
              <span className="text-lg flex-shrink-0">{warning.icon}</span>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-sm font-rajdhani">
                  {warning.title}
                </p>
                <p className="text-xs text-gray-300 mt-0.5 font-rajdhani">
                  {warning.message}
                </p>
                {warning.suggestion && (
                  <p className="text-xs text-gray-400 mt-1 font-rajdhani">
                    💡 {warning.suggestion}
                  </p>
                )}
              </div>
              {warning.action && (
                <button
                  onClick={warning.action.onClick}
                  className="flex-shrink-0 px-3 py-1 text-xs font-rajdhani rounded bg-white/10 hover:bg-white/20 transition-colors"
                >
                  {warning.action.label}
                </button>
              )}
            </div>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}

// Compact version for inline use
export function BuyWarningBadge({ warning }: { warning: BuyWarning }) {
  return (
    <span
      className={`
        inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-rajdhani
        ${WARNING_STYLES[warning.type]}
      `}
      title={warning.message}
    >
      <span>{warning.icon}</span>
      <span>{warning.title}</span>
    </span>
  );
}

export default BuyKeysWarnings;
